import React, { useCallback, useEffect, useState } from "react";
import { X, ClipboardCheck, Loader, CheckCircle, Link, Lock, Plus } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import SignaturePad from "../UI/SignaturePad";
import { pohAPI, shipmentAccessAPI } from "../../services/api";
import { FileDropzone, GeoStatusBanner, useGeoCapture } from "../poh/PohEvidence";

type EmailTag = { email: string; role: "view" | "poh_handler" };

interface AccessSectionProps {
  shipmentViewUrl: string;
  linkCopied: boolean;
  copyLink: () => void;
  emailTags: EmailTag[];
  setEmailTags: React.Dispatch<React.SetStateAction<EmailTag[]>>;
  autoSaveEmails: (tags: EmailTag[]) => void;
  emailTagInput: string;
  setEmailTagInput: React.Dispatch<React.SetStateAction<string>>;
  addEmail: () => void;
  savingEmails: boolean;
}

const AccessSection: React.FC<AccessSectionProps> = ({
  shipmentViewUrl, linkCopied, copyLink,
  emailTags, setEmailTags, autoSaveEmails,
  emailTagInput, setEmailTagInput, addEmail, savingEmails,
}) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <Link className="h-3.5 w-3.5 text-gray-500" />
        <span className="text-xs font-semibold text-gray-700">Client Shipment Link</span>
      </div>
      {savingEmails && <span className="text-[10px] text-gray-400 animate-pulse">Saving…</span>}
    </div>
    <div className="p-3 space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={shipmentViewUrl}
          readOnly
          className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded-md bg-white text-blue-600 select-all"
        />
        <button
          type="button"
          onClick={copyLink}
          className={`px-3 py-1.5 text-xs rounded-md border font-medium transition-colors whitespace-nowrap ${linkCopied ? "bg-green-600 text-white border-green-600" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
        >
          {linkCopied ? "Copied!" : "Copy"}
        </button>
      </div>

      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <Lock className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-xs font-medium text-gray-700">
            {emailTags.length > 0
              ? `${emailTags.length} email${emailTags.length > 1 ? "s" : ""} with access`
              : "Add emails to grant access to this link"}
          </span>
        </div>

        {emailTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {emailTags.map((entry) => (
              <span
                key={entry.email}
                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
                  entry.role === "poh_handler"
                    ? "bg-purple-50 text-purple-800 border-purple-200"
                    : "bg-blue-50 text-blue-800 border-blue-200"
                }`}
              >
                <span>{entry.email}</span>
                <span className={`text-[10px] px-1 rounded-full font-medium ${
                  entry.role === "poh_handler" ? "bg-purple-200 text-purple-900" : "bg-blue-200 text-blue-900"
                }`}>
                  {entry.role === "poh_handler" ? "POH" : "Standard"}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const next = emailTags.map((e) =>
                      e.email === entry.email
                        ? { ...e, role: (e.role === "view" ? "poh_handler" : "view") as "view" | "poh_handler" }
                        : e,
                    );
                    setEmailTags(next);
                    autoSaveEmails(next);
                  }}
                  className="hover:text-amber-600 transition-colors"
                  title="Toggle role"
                >
                  <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = emailTags.filter((e) => e.email !== entry.email);
                    setEmailTags(next);
                    autoSaveEmails(next);
                  }}
                  className="hover:text-red-600 transition-colors"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1.5">
          <input
            type="email"
            value={emailTagInput}
            onChange={(e) => setEmailTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmail(); } }}
            placeholder="email@example.com — press Enter to add"
            className="flex-1 text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-400 focus:border-blue-400 placeholder-gray-400"
          />
          <button
            type="button"
            onClick={addEmail}
            className="p-1.5 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-600"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

interface Props {
  shipmentId: string;
  shipmentNumber: string;
  pohLevel?: 0 | 1 | 2;
  onClose: () => void;
}

type Step = "sign" | "qr";

const PickupHandoverModal: React.FC<Props> = ({ shipmentId, shipmentNumber, pohLevel = 1, onClose }) => {
  // Level 2 has no driver signature — the shipper's signature alone locks the
  // pickup, so there's no QR step.
  const isLevel2 = pohLevel === 2;
  const [step, setStep] = useState<Step>("sign");
  const [signerName, setSignerName] = useState("");
  const [signatureData, setSignatureData] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [driverSignUrl, setDriverSignUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const geo = useGeoCapture(true);

  // Shipment link access
  const shipmentViewUrl = `${window.location.origin}/shipment-clients-view?shipmentId=${shipmentId}`;
  const [emailTags, setEmailTags] = useState<{ email: string; role: "view" | "poh_handler" }[]>([]);
  const [emailTagInput, setEmailTagInput] = useState("");
  const emailTagRole: "view" | "poh_handler" = "poh_handler";
  const [savingEmails, setSavingEmails] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    shipmentAccessAPI.getAccessRequests(shipmentId)
      .then((res) => setEmailTags(res.data.data.allowedEmails ?? []))
      .catch(() => {});
  }, [shipmentId]);

  const autoSaveEmails = useCallback(async (tags: { email: string; role: "view" | "poh_handler" }[]) => {
    setSavingEmails(true);
    try {
      await shipmentAccessAPI.updateAllowedEmails(shipmentId, tags);
    } catch {
      toast.error("Failed to update access");
    } finally {
      setSavingEmails(false);
    }
  }, [shipmentId]);

  const addEmail = () => {
    const val = emailTagInput.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { toast.error("Enter a valid email"); return; }
    if (emailTags.some((t) => t.email === val)) { toast.error("Email already added"); return; }
    const next = [...emailTags, { email: val, role: emailTagRole }];
    setEmailTags(next);
    setEmailTagInput("");
    autoSaveEmails(next);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shipmentViewUrl); } catch { return; }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signerName.trim()) return toast.error("Please enter the consigner name");
    if (!signatureData) return toast.error("Please provide a signature");
    if (geo.state.status !== "ok") return toast.error("Location is required. Allow location access and retry.");

    try {
      setSubmitting(true);
      const res = await pohAPI.consignerSign(shipmentId, { signerName, signatureData }, geo.state.geo, files);
      const { driverSignUrl: url, expiresAt: exp } = res.data.data;
      if (isLevel2) {
        toast.success("Pickup handover signed and locked.");
        onClose();
        return;
      }
      setDriverSignUrl(url);
      setExpiresAt(exp);
      setStep("qr");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to record signature");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">Pickup Handover</h2>
              <p className="text-xs text-gray-500">{shipmentNumber}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Step indicator */}
        <div className={`flex border-b border-gray-100 shrink-0 ${isLevel2 ? "hidden" : ""}`}>
          {(["sign", "qr"] as Step[]).map((s, i) => (
            <div
              key={s}
              className={`flex-1 py-2 text-center text-xs font-medium ${
                step === s ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-400"
              }`}
            >
              {i + 1}. {s === "sign" ? "Consigner Signs" : "Driver Scans QR"}
            </div>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">
          {/* Access section always at top */}
          <div className="px-5 pt-4">
            <AccessSection
              shipmentViewUrl={shipmentViewUrl}
              linkCopied={linkCopied}
              copyLink={copyLink}
              emailTags={emailTags}
              setEmailTags={setEmailTags}
              autoSaveEmails={autoSaveEmails}
              emailTagInput={emailTagInput}
              setEmailTagInput={setEmailTagInput}
              addEmail={addEmail}
              savingEmails={savingEmails}
            />
          </div>

          {step === "sign" && (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                {isLevel2
                  ? "The consigner (sender) signs below to confirm the goods are handed over. This locks the pickup — no driver signature is required."
                  : "The consigner (sender) signs below to confirm goods are being handed over to the driver."}
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consigner Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Full name of person handing over goods"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <SignaturePad
                label="Consigner Signature"
                onSave={setSignatureData}
                width={600}
                height={200}
              />

              <GeoStatusBanner state={geo.state} onRetry={geo.capture} />

              <FileDropzone files={files} onChange={setFiles} disabled={submitting} />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || geo.state.status !== "ok"}
                  className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {submitting && <Loader className="h-3.5 w-3.5 animate-spin" />}
                  {isLevel2 ? "Confirm & Lock Pickup" : "Confirm & Generate QR"}
                </button>
              </div>
            </form>
          )}

          {step === "qr" && (
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg px-3 py-2">
                <CheckCircle className="h-4 w-4 shrink-0" />
                <p className="text-sm font-medium">Consigner signature recorded successfully.</p>
              </div>

              <div className="text-center space-y-3">
                <p className="text-sm text-gray-700 font-medium">
                  Ask the driver to scan this QR code to sign as picked up
                </p>
                <div className="inline-flex p-4 bg-white border-2 border-gray-200 rounded-xl shadow-sm">
                  <QRCodeSVG value={driverSignUrl} size={200} level="M" />
                </div>
                <p className="text-xs text-gray-400">
                  QR expires: {expiresAt ? new Date(expiresAt).toLocaleString() : "48 hours"}
                </p>
                <p className="text-xs text-gray-500">
                  Or share this link: <span className="text-blue-600 break-all">{driverSignUrl}</span>
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm rounded-md bg-gray-900 text-white font-medium hover:bg-gray-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickupHandoverModal;
